import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../Utils/api";
import Select from "react-select";
import Spinner from "../Components/Spinner";
import AddEditCheckpointModal from "../Components/CheckPoint/AddEditCheckpointModal";
import ArchiveCheckpointModal from "../Components/CheckPoint/ArchiveCheckpointModal";
import QRCodeModal from "../Components/CheckPoint/QRCodeModal";
import NFCDetailsModal from "../Components/CheckPoint/NFCDetailsModal";
import QRImage from "../Assets/image/qrcode.png"
import NFCImage from "../Assets/image/nfc.png"

export default function Checkpoints() {
  const user = useSelector((state) => state.user.user);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [checkpoints, setCheckpoints] = useState([]);
  const [filteredCheckpoints, setFilteredCheckpoints] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showNFCModal, setShowNFCModal] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);

  useEffect(() => {
    if (user) {
      getCheckpoints();
      getLocations();
      if (user.deputyAccessLevel === "system_administrator") {
        getCompanies();
      }
    }
  }, [user]);

  useEffect(() => {
    filterCheckpoints();
  }, [checkpoints, selectedCompany, selectedLocation, searchTerm]);

  const getCheckpoints = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/checkpoint");
      setIsLoading(false);
      if (response.data.success) {
        setCheckpoints(response.data.checkpoints || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch checkpoints");
      setIsLoading(false);
    }
  };

  const getLocations = async () => {
    try {
      const response = await api.get("/location");
      if (response.data.locations) {
        setLocations(response.data.locations.filter((loc) => !loc.archive));
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch locations");
    }
  };

  const getCompanies = async () => {
    try {
      const response = await api.get("/company");
      if (response.data.success) {
        // Only show active (non-archived) companies
        setCompanies((response.data.companies || []).filter(company => !company.archive));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const filterCheckpoints = () => {
    let filtered = checkpoints;

    // Filter by company
    if (selectedCompany) {
      filtered = filtered.filter((cp) => {
        const cpCompanyID = cp.companyID?._id || cp.companyID;
        return cpCompanyID && cpCompanyID.toString() === selectedCompany._id.toString();
      });
    }

    // Filter by location
    if (selectedLocation) {
      filtered = filtered.filter((cp) => {
        const cpLocationID = cp.locationID?._id || cp.locationID;
        return cpLocationID && cpLocationID.toString() === selectedLocation._id.toString();
      });
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (cp) =>
          cp.name?.toLowerCase().includes(search) ||
          cp.code?.toLowerCase().includes(search) ||
          cp.type?.toLowerCase().includes(search) ||
          cp.locationID?.name?.toLowerCase().includes(search) ||
          cp.ID?.toString().includes(search)
      );
    }

    setFilteredCheckpoints(filtered);
  };

  const handleAdd = () => {
    setSelectedCheckpoint(null);
    setShowAddModal(true);
  };

  const handleEdit = (checkpoint) => {
    navigate(`/checkpoints/edit/${checkpoint._id}`);
  };

  const handleSave = async (formData) => {
    try {
      if (!formData.name || !formData.locationID) {
        toast.error("Name and Location are required");
        return;
      }

      setIsLoading(true);
      const data = {
        ...formData,
        locationID: formData.locationID,
      };

      // Create
      const response = await api.post("/checkpoint", data);
      if (response.data.success) {
        toast.success("Checkpoint created successfully");
        getCheckpoints();
        setShowAddModal(false);
      } else {
        toast.error(response.data.message || "Failed to create checkpoint");
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save checkpoint");
      setIsLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      if (!selectedCheckpoint) return;

      setIsLoading(true);
      const response = await api.post(`/checkpoint/archive/${selectedCheckpoint._id}`);
      setIsLoading(false);

      if (response.data.success) {
        toast.success("Checkpoint archived successfully");
        getCheckpoints();
        setShowArchiveModal(false);
        setSelectedCheckpoint(null);
      } else {
        toast.error(response.data.message || "Failed to archive checkpoint");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to archive checkpoint");
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      if (!selectedCheckpoint) return;

      setIsLoading(true);
      const response = await api.post(`/checkpoint/restore/${selectedCheckpoint._id}`);
      setIsLoading(false);

      if (response.data.success) {
        toast.success("Checkpoint restored successfully");
        getCheckpoints();
        setShowArchiveModal(false);
        setSelectedCheckpoint(null);
      } else {
        toast.error(response.data.message || "Failed to restore checkpoint");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to restore checkpoint");
      setIsLoading(false);
    }
  };

  const handleViewQR = (checkpoint) => {
    if (checkpoint.type === "QR") {
      setSelectedCheckpoint(checkpoint);
      setShowQRModal(true);
    } else {
      toast.info("NFC checkpoints don't have QR codes");
    }
  };

  const handleViewNFC = (checkpoint) => {
    if (checkpoint.type === "NFC") {
      setSelectedCheckpoint(checkpoint);
      setShowNFCModal(true);
    } else {
      toast.info("Only NFC checkpoints have NFC details");
    }
  };

  const locationOptions = locations.map((loc) => ({
    _id: loc._id,
    name: loc.name,
    label: loc.name,
    value: loc._id,
  }));

  const companyOptions = companies.map((comp) => ({
    _id: comp._id,
    name: comp.name,
    label: comp.name,
    value: comp._id,
  }));

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">Checkpoints</h1>
        <button
          onClick={handleAdd}
          className="bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600 transition"
        >
          <i className="fa fa-plus mr-2"></i>
          Add Checkpoint
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4 items-center flex-wrap">
        {/* Company Filter - Only for system administrators */}
        {user && user.deputyAccessLevel === "system_administrator" && (
          <div className="flex gap-2 items-center">
            <label className="text-sm font-medium text-gray-700">Company:</label>
            <div className="w-64">
              <Select
                name="company"
                placeholder="All Companies"
                options={companyOptions}
                value={selectedCompany}
                onChange={(option) => setSelectedCompany(option)}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option._id}
                isClearable={true}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#9ca3af" },
                  }),
                }}
              />
            </div>
          </div>
        )}

        {/* Location Filter */}
        <div className="flex gap-2 items-center">
          <label className="text-sm font-medium text-gray-700">Location:</label>
          <div className="w-64">
            <Select
              name="location"
              placeholder="All Locations"
              options={locationOptions}
              value={selectedLocation}
              onChange={(option) => {
                setSelectedLocation(option);
              }}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option._id}
              isClearable={true}
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderColor: "#d1d5db",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#9ca3af" },
                }),
              }}
            />
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 items-center flex-1">
          <label className="text-sm font-medium text-gray-700">Search:</label>
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search by name, code, type, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border border-gray-200 overflow-hidden">
        <div className="overflow-y-auto max-h-[77vh] pb-20">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-gray-200 z-10">
            <tr>
              <th className="p-4 text-left font-bold text-gray-700">ID</th>
              <th className="p-4 text-left font-bold text-gray-700">Name</th>
              <th className="p-4 text-center font-bold text-gray-700">Type</th>
              <th className="p-4 text-left font-bold text-gray-700">Location</th>
              <th className="p-4 text-left font-bold text-gray-700">Code</th>
              <th className="p-4 text-left font-bold text-gray-700">Company</th>
              <th className="p-4 text-center font-bold text-gray-700">Status</th>
              <th className="p-4 text-center font-bold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCheckpoints.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-4 text-center text-gray-500">
                  No checkpoints found
                </td>
              </tr>
            ) : (
              filteredCheckpoints.map((checkpoint) => (
                <tr key={checkpoint._id} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="p-4 text-gray-700">
                    {checkpoint.ID?.toString().padStart(6, "0") || "-"}
                  </td>
                  <td className="p-4 font-medium text-gray-700">{checkpoint.name}</td>
                  <td className="p-4 text-center">
                    {checkpoint.type === "GPS" ? (
                      <i className="fa fa-map-marker-alt text-2xl text-blue-600" title="GPS"></i>
                    ) : (
                      <img
                        src={checkpoint.type === "QR" ? QRImage : NFCImage}
                        alt={checkpoint.type}
                        className="w-8 h-8 mx-auto object-contain"
                        title={checkpoint.type}
                      />
                    )}
                  </td>
                  <td className="p-4 text-gray-600">
                    {checkpoint.locationID?.name || "-"}
                  </td>
                  <td className="p-4 text-gray-600 font-mono text-sm">
                    {checkpoint.type === "GPS" ? "-" : (checkpoint.code || "-")}
                  </td>
                  <td className="p-4 text-gray-600">
                    {checkpoint.companyID?.name || "-"}
                  </td>
                  <td className="p-4 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        checkpoint.archive
                          ? "bg-gray-200 text-gray-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {checkpoint.archive ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2 justify-center">
                      {checkpoint.type === "QR" && (
                        <button
                          onClick={() => handleViewQR(checkpoint)}
                          className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          title="View QR Code"
                        >
                          <i className="fa fa-qrcode mr-1"></i>
                          QR
                        </button>
                      )}
                      {checkpoint.type === "NFC" && (
                        <button
                          onClick={() => handleViewNFC(checkpoint)}
                          className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                          title="View NFC Details"
                        >
                          <i className="fa fa-wifi mr-1"></i>
                          NFC
                        </button>
                      )}
                      {checkpoint.type === "GPS" && (
                        <button
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded cursor-default"
                          title="GPS Location Checkpoint"
                          disabled
                        >
                          <i className="fa fa-map-marker-alt mr-1"></i>
                          GPS
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(checkpoint)}
                        className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                        title="Edit"
                      >
                        <i className="fa fa-edit mr-1"></i>
                        Edit
                      </button>
                      {checkpoint.archive ? (
                        <button
                          onClick={() => {
                            setSelectedCheckpoint(checkpoint);
                            setShowArchiveModal(true);
                          }}
                          className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                          title="Restore"
                        >
                          <i className="fa fa-undo mr-1"></i>
                          Restore
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedCheckpoint(checkpoint);
                            setShowArchiveModal(true);
                          }}
                          className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                          title="Archive"
                        >
                          <i className="fa fa-archive mr-1"></i>
                          Archive
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      <AddEditCheckpointModal
        show={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedCheckpoint(null);
        }}
        onSave={handleSave}
        checkpoint={null}
        locations={locations}
        isLoading={isLoading}
      />

      {/* Archive/Restore Confirmation Modal */}
      <ArchiveCheckpointModal
        show={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setSelectedCheckpoint(null);
        }}
        onConfirm={selectedCheckpoint?.archive ? handleRestore : handleArchive}
        checkpoint={selectedCheckpoint}
        isLoading={isLoading}
      />

      {/* QR Code Modal */}
      <QRCodeModal
        show={showQRModal}
        onClose={() => {
          setShowQRModal(false);
          setSelectedCheckpoint(null);
        }}
        checkpoint={selectedCheckpoint}
      />

      {/* NFC Details Modal */}
      <NFCDetailsModal
        show={showNFCModal}
        onClose={() => {
          setShowNFCModal(false);
          setSelectedCheckpoint(null);
        }}
        checkpoint={selectedCheckpoint}
      />
    </div>
  );
}

